import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';
import { candidateAPI } from '../../../services';
import CTCBuilder from './CTCBuilder';

const HROfferDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ctcPending, setCtcPending] = useState([]);
  const [offerSent, setOfferSent] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [declined, setDeclined] = useState([]);
  const [offerTarget, setOfferTarget] = useState(null);
  const [offerData, setOfferData] = useState({});
  const [offerBreakup, setOfferBreakup] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [issuedCredentials, setIssuedCredentials] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [ctcRes, sentRes, acceptedRes, declinedRes] = await Promise.all([
        candidateAPI.getCandidates({ stage: 'ctc_round' }),
        candidateAPI.getCandidates({ stage: 'offer_sent' }),
        candidateAPI.getCandidates({ stage: 'accepted' }),
        candidateAPI.getCandidates({ stage: 'declined' })
      ]);
      setCtcPending(ctcRes.data?.candidates || []);
      setOfferSent(sentRes.data?.candidates || []);
      setAccepted(acceptedRes.data?.candidates || []);
      setDeclined(declinedRes.data?.candidates || []);
    } catch (err) {
      setError(err.message || 'Failed to load offers data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const feedbackFor = (candidate, roundType) => {
    const interview = [...(candidate.interviews || [])].reverse().find(i => i.roundType === roundType && i.feedback?.decision);
    return interview?.feedback;
  };

  const openOfferForm = (candidate) => {
    setOfferTarget(candidate);
    setOfferData({
      designation: candidate.job?.title || candidate.currentDesignation || '',
      overrides: {}
    });
    setOfferBreakup(null);
  };

  const submitOffer = async () => {
    if (!offerTarget) return;
    try {
      setSubmitting(true);
      setError('');
      const res = await candidateAPI.releaseOffer(offerTarget._id, {
        ctcInput: parseFloat(offerData.ctcInput),
        overrides: offerData.overrides || {},
        joiningDate: offerData.doj,
        designation: offerData.designation,
        department: offerData.department,
        location: offerData.location
      });
      setIssuedCredentials(res.data?.credentials || null);
      setOfferTarget(null);
      setOfferData({});
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to send offer');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">CTC & Offers</h1>
        <p className="text-gray-500 mt-1">Finalize compensation, generate Candidate Portal credentials, and send offers.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {issuedCredentials && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="font-semibold text-green-800 mb-1">Offer sent - Candidate Portal credentials</p>
          <p className="text-sm text-green-700">
            Username: <strong>{issuedCredentials.username}</strong> • Temporary Password: <strong>{issuedCredentials.password}</strong>
          </p>
          <p className="text-xs text-green-600 mt-1">These credentials were also emailed to the candidate. This is the only time the password is shown here.</p>
          <button onClick={() => setIssuedCredentials(null)} className="text-xs text-green-700 underline mt-2">Dismiss</button>
        </div>
      )}

      {/* CTC Pending */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-yellow-600" /> CTC Pending ({ctcPending.length})
        </h3>
        {ctcPending.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No candidates awaiting an offer</p>
        ) : (
          <div className="space-y-3">
            {ctcPending.map(candidate => {
              const managerFeedback = feedbackFor(candidate, 'manager');
              const hodFeedback = feedbackFor(candidate, 'hod');
              return (
                <div key={candidate._id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                    <p className="text-sm text-gray-500">{candidate.job?.title}</p>
                    <div className="flex gap-4 mt-2 text-xs text-gray-600">
                      {managerFeedback && <span>Manager: {managerFeedback.rating}/5</span>}
                      {hodFeedback && <span>HOD: {hodFeedback.rating}/5</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => openOfferForm(candidate)}
                    className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Create Offer
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Offers Tracker */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">Offers Tracker</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Sent ({offerSent.length})</h4>
            {offerSent.map(c => (
              <div key={c._id} className="text-sm p-2 bg-indigo-50 rounded mb-1">
                {c.name} - ₹{(c.offer?.ctcBreakup?.ctcInput || c.offer?.offeredCTC?.amount)?.toLocaleString()}
                {c.offer?.ctcBreakup?.band?.id ? ` (Band ${c.offer.ctcBreakup.band.id})` : ''}
              </div>
            ))}
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Accepted ({accepted.length})</h4>
            {accepted.map(c => (
              <div key={c._id} className="text-sm p-2 bg-green-50 rounded mb-1">{c.name}</div>
            ))}
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1"><XCircle className="w-3 h-3 text-red-600" /> Declined ({declined.length})</h4>
            {declined.map(c => (
              <div key={c._id} className="text-sm p-2 bg-red-50 rounded mb-1">
                {c.name}{c.candidateDecision?.reason ? ` - ${c.candidateDecision.reason}` : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create Offer Modal */}
      {offerTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setOfferTarget(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h4 className="font-semibold text-gray-900 mb-3">Create Offer for {offerTarget.name}</h4>

            <div className="space-y-3">
              <CTCBuilder
                ctcInput={offerData.ctcInput}
                overrides={offerData.overrides || {}}
                onCtcInputChange={(value) => setOfferData({ ...offerData, ctcInput: value })}
                onOverridesChange={(overrides) => setOfferData({ ...offerData, overrides })}
                onBreakupChange={setOfferBreakup}
              />
              <div>
                <label className="block text-sm text-gray-700 mb-1">Date of Joining</label>
                <input
                  type="date"
                  value={offerData.doj || ''}
                  onChange={(e) => setOfferData({ ...offerData, doj: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Designation</label>
                <input
                  type="text"
                  value={offerData.designation || ''}
                  onChange={(e) => setOfferData({ ...offerData, designation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={offerData.department || ''}
                  onChange={(e) => setOfferData({ ...offerData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Work Location</label>
                <input
                  type="text"
                  value={offerData.location || ''}
                  onChange={(e) => setOfferData({ ...offerData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={submitOffer}
                disabled={submitting || !offerData.ctcInput || !offerData.doj || offerBreakup?.isOverAllocated}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send Offer Letter'}
              </button>
              <button
                onClick={() => setOfferTarget(null)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HROfferDashboard;
